import { visionConfig, visionSocketUrl } from '@ctcells/core'
import { t } from '@ctcells/i18n'
import { useEffect, useRef, useState } from 'react'

import {
  connectVisionStream,
  type VisionStreamHandle,
  type VisionStreamState,
} from './visionStreamClient.ts'

import './figures.css'

/**
 * 비전 카메라 **실영상**. 중계기(go2rtc)가 RTSP 를 받아 WebRTC · MSE 로 다시 내고, 이 컴포넌트가
 * 그중 되는 길로 붙는다(`visionStreamClient.ts`).
 *
 * 🔴 **판정 박스를 그리지 않는다.** 들어오는 것은 영상이지 판정이 아니다 — 칸별 판정은 서버가
 *    주지 않는 축이라(BR-31) 여기에 박스를 얹으면 **없는 판정을 발명**하는 것이 된다.
 * 🔴 **해상도는 `<video>` 가 말해 주는 값만** 적는다. 설정값이나 짐작을 적지 않는다.
 * 🔴 **표준 웹 API 다 — 플랫폼 계층이 필요 없다.** 웹과 Tauri 가 같은 코드로 돈다.
 *    (「웹에서는 어떻게 되나」의 답: 그대로 된다. WebRTC 가 없는 웹뷰는 MSE 로 내려간다)
 *
 * 경계 상태는 셋이 여기 있고(연결 중 · 재생 중 · 연결 실패), **스트림 미설정**은 아예 이
 * 컴포넌트가 서지 않는 것으로 답한다(`FocusFigure` 가 그 자리에 종전 문구를 적는다).
 */
export interface VisionStreamProps {
  /** go2rtc 스트림 이름. 설정에서 온다 — 화면이 짓지 않는다 */
  stream: string
  /** 어느 모듈의 카메라인지 — 영상 서버가 모듈마다 다를 수 있어 기준 경로를 이것으로 고른다 */
  moduleKey: string
  /** 그림 이름(접근성 라벨) */
  label: string
  /**
   * 연결 상태를 바깥에 알린다 — 영상 칸 머리의 **연결 상태 배지**가 읽는다(모니터링 비전 열 · 디자인팀 시안 2026-10-01 §3).
   * 🔴 이 컴포넌트가 아는 사실(연결 중 · 재생 중 · 실패와 그 길 · 해상도)을 그대로 넘길 뿐이다 — 바깥이 짓지 않는다.
   */
  onState?: ((state: VisionStreamState) => void) | undefined
  /**
   * 영상 위 정보 글자(`● LIVE  WebRTC  1280×720`)를 그릴지. 기본 그린다.
   * 바깥이 같은 사실을 머리 줄(배지 · 길 · 해상도)로 이미 말할 때만 끈다 — 같은 말을 두 번 하지 않는다.
   */
  meta?: boolean | undefined
}

export function VisionStream({
  stream,
  moduleKey,
  label,
  onState,
  meta = true,
}: VisionStreamProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [state, setState] = useState<VisionStreamState>({
    phase: 'connecting',
    mode: undefined,
    reason: undefined,
    size: undefined,
  })

  useEffect(() => {
    const video = videoRef.current
    if (video === null) return

    const config = visionConfig()
    let handle: VisionStreamHandle | undefined

    const open = (): void => {
      handle ??= connectVisionStream({
        url: visionSocketUrl(stream, moduleKey, config),
        video,
        negotiateTimeoutMs: config.negotiateTimeoutMs,
        reconnectDelayMs: config.reconnectDelayMs,
        onState: setState,
      })
    }
    const stop = (): void => {
      handle?.close()
      handle = undefined
    }

    /*
     * 🔴 **안 보이면 끊는다.** 모듈 셋 × 별창까지 열리면 중계기에 소비자가 쌓인다.
     *    그림 탭을 떠나면 이 컴포넌트가 언마운트되어 아래 정리가 돌고, 창을 가리거나
     *    다른 탭으로 가면 이 핸들러가 끊는다.
     */
    const onVisibility = (): void => {
      if (document.visibilityState === 'hidden') stop()
      else open()
    }
    document.addEventListener('visibilitychange', onVisibility)
    if (document.visibilityState !== 'hidden') open()

    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
      stop()
    }
  }, [stream, moduleKey])

  /* 바깥 알림 — 렌더 중이 아니라 상태가 바뀐 뒤에 부른다 */
  useEffect(() => {
    onState?.(state)
  }, [onState, state])

  const none = t('mask.none')
  const resolution =
    state.size === undefined ? none : `${String(state.size.width)}×${String(state.size.height)}`
  // 길 이름(WebRTC · MSE)은 고유명사라 번역하지 않는다 — 제품명과 같은 규칙(README §15)
  const mode = state.mode === undefined ? none : state.mode === 'webrtc' ? 'WebRTC' : 'MSE'

  return (
    <div className="fig-vision fig-vstream">
      <video
        aria-label={label}
        autoPlay
        className="fig-vstream__video"
        muted
        playsInline
        ref={videoRef}
      />
      {meta ? (
        <p className="fig-vstream__meta">
          {`${state.phase === 'playing' ? '●' : '○'} ${t('monitoring.live')}  ${mode}  ${resolution}`}
        </p>
      ) : null}
      {state.phase === 'playing' ? null : (
        <p className="fig-vstream__note" role="status">
          {state.phase === 'connecting' ? (
            <span aria-hidden="true" className="fig-vstream__spinner" />
          ) : null}
          {state.phase === 'connecting'
            ? t('vision.badge.connecting')
            : state.reason === 'unsupported'
              ? t('vision.unsupported')
              : t('vision.badge.failed')}
        </p>
      )}
    </div>
  )
}
